/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import i18n from 'i18n-react';
import { Spring } from 'react-spring';

import badges from 'assets/images/badges';

import BadgeDisplay from 'components/BadgeDisplay';
import IntroChapterPopup from 'components/IntroChapterPopup';
import Progress from 'components/Progress';
import TrainingCard from 'components/TrainingGrid/TrainingCard';
import TrainingCategoryCard from 'components/TrainingGrid/TrainingCategoryCard';
import TrainingExtraCard from 'components/TrainingGrid/TrainingExtraCard';
import TrainingGridAccordion from 'components/TrainingGrid/TrainingGridAccordion';

import { useChapters } from 'hooks/useChapters';
import { usePointsHandler } from 'hooks/usePointsHandler';
import { useSettings } from 'hooks/useSettings';

const SubProgressBar = ({ type = null, progress }) => {
  const roundedProgress = Math.round(progress);

  return (
    <div className={`subprogress ${type}`}>
      <Progress id="subprogress" value={roundedProgress} rounded />
      <p>
        {progress} {i18n.translate('trainingScreens.percentageComplete')}
      </p>
    </div>
  );
};

SubProgressBar.propTypes = {
  progress: PropTypes.number.isRequired,
  type: PropTypes.oneOf([null, 'bronze', 'silver', 'gold', 'platinum']),
};

const TrainingGrid = () => {
  const {
    appId,
    lang: { code, langFile },
  } = useSettings();
  const {
    chapters,
    categories,
    groups,
    subapps,
    groups: { main, sub },
    loaded: chaptersLoaded,
  } = useChapters();

  // calculates the diploma progress rate based on the chapters that are in the main group.
  const diplomaProgressRate = useMemo(() => {
    const {
      main: { chapterIDs },
    } = groups;

    const { totalComplete, totalRelevant } = chapters.reduce(
      (acc, chapter) => {
        const { id, percentage } = chapter;
        if (chapterIDs.includes(id)) {
          acc.totalRelevant += 1;
          if (percentage === 100) acc.totalComplete += 1;
        }
        return acc;
      },
      { totalComplete: 0, totalRelevant: 0 },
    );

    if (totalRelevant > 0) return Math.round((totalComplete / totalRelevant) * 100);

    return 0;
  }, [chapters, groups]);

  const { tiers, totalPoints, getTieredValue, setPoints, loaded: pointsHandlerLoaded } = usePointsHandler();
  const tierIndex = tiers && Math.min(getTieredValue(), tiers.length - 1);

  const isDataReady = useMemo(
    () => pointsHandlerLoaded && chapters.length > 0 && categories.length > 0 && subapps.length > 0,
    [pointsHandlerLoaded, chapters, categories, subapps],
  );

  /**
   * Handles the point updates for chapters and subapps. Has 3 types of behavior:
   *
   * 1. Foundational chapters: For single, independant chapters, the points are to be updated for each completed chapter
   *    with the action: `intro-chapter` and value: `intro-chapter-${chapter.id}.
   * 2. Grouped catogories (e.g. Big City Buzz), the points are to be updated ONCE for each fully completed collection of
   *    chapters with the action: `specialization` and value: `specialization-${category.id}`
   * 3. Grouped subapps (e.g. partner modules), the points are to be updated for each individual completed modules with the
   *    action: `subapp` and value: `subapp-${subapp.sessionAppId}`
   */
  useEffect(() => {
    const handlePointUpdates = async () => {
      // Gather the foundational chapters
      const { foundationalChapters } = chapters.reduce(
        (acc, chapter) => {
          const { id, percentage } = chapter;
          // If the chapter is in the main group and is completed, add it to the foundational chapters
          if (main.chapterIDs.includes(id) && percentage === 100) {
            acc.foundationalChapters.push(chapter);
            // if the chapter is in the sub group and is completed, add it to the specialist chapters
          }
          return acc;
        },
        { foundationalChapters: [] },
      );

      const { experienceCategories } = categories.reduce(
        (acc, category) => {
          const { id } = category;
          if (sub.categoryIDs.includes(id)) {
            acc.experienceCategories.push(category);
          }
          return acc;
        },
        { experienceCategories: [] },
      );

      // Since updates are done on a per-module basis, we need to flatten the subapps array to get all the modules.
      const subappModules = subapps.flatMap(({ modules }) => modules);

      const foundationalUpdates = foundationalChapters
        .filter(({ percentage, id }) => {
          const isComplete = percentage === 100;
          const hasValidId = !Number.isNaN(id);
          return isComplete && hasValidId;
        })
        .map(({ id }) => ({ action: 'intro-chapter', value: `intro-chapter-${id}` }));

      const experienceUpdates = experienceCategories
        .filter(({ percentage, id }) => {
          const isComplete = percentage === 100;
          const hasValidId = !Number.isNaN(id);
          return isComplete && hasValidId;
        })
        .map(({ id }) => ({ action: 'specialization', value: `specialization-${id}` }));

      const subappUpdates = subappModules
        .filter(({ percentage, sessionAppId }) => {
          const isComplete = percentage === 100;
          const hasValidId = !Number.isNaN(sessionAppId);
          return isComplete && hasValidId;
        })
        .map(({ sessionAppId }) => ({ action: 'subapp', value: `subapp-${sessionAppId}` }));

      const allUpdates = [...foundationalUpdates, ...experienceUpdates, ...subappUpdates];

      await setPoints(allUpdates);
    };
    if (isDataReady) {
      handlePointUpdates();
    }
  }, [categories, chapters, isDataReady, main.chapterIDs, setPoints, sub.categoryIDs, subapps]);

  // takes a list of chapters and returns the total number of chapters and the number of completed chapters.
  const getItemsFromChapters = (chapterList) =>
    chapterList.reduce(
      (acc, chapter) => {
        const { percentage } = chapter;
        acc.totalItemCount += 1;
        if (percentage === 100) acc.totalItemCountCompleted += 1;
        return acc;
      },
      { totalItemCountCompleted: 0, totalItemCount: 0 },
    );

  const isLockedChapter = useCallback(
    (id) => {
      // if chapters aren't loaded, this means that the id is locked by default.
      // eslint-disable-next-line no-unreachable
      if (!chapters?.length > 0) return true;

      // if chapters aren't found, this means that chapters are locked by default.
      const relevantChapter = chapters.find(({ id: chapterId }) => chapterId === id);
      if (!relevantChapter) return true;

      const { chapterNumber, chapterItemCount: totalPages } = relevantChapter;

      // chapters without pages are locked
      if (totalPages === 0) return true;

      // if the chapter is the first chapter, it should be unlocked.
      if (chapterNumber === 1 || chapterNumber === 2) return false;

      const previousChapter = chapters.find(
        ({ chapterNumber: prevChapterNumber }) => Number(prevChapterNumber) === Number(chapterNumber) - 1,
      );

      // if the previous chapter is completed (percentage === 100), the chapter should be unlocked.
      const { percentage: previousChapterPercentage } = previousChapter || {};
      return previousChapterPercentage !== 100;
    },
    [chapters],
  );

  // calculates the completion rate of a category based on the chapters in the category
  const calculateGroupCompletionRate = (group) => {
    const { chapterIDs = [] } = group || {};
    const groupChapters = chapters.filter(({ id }) => chapterIDs.includes(id));

    const { totalItemCountCompleted, totalItemCount } = getItemsFromChapters(groupChapters);

    if (totalItemCount === 0) return 0;

    return Math.round((totalItemCountCompleted / totalItemCount) * 100);
  };

  // const { totalItemCountCompleted } = useMemo(() => getItemsFromChapters(chapters), [chapters]);

  const foundationalCompletionRate = calculateGroupCompletionRate(main);
  const specialistCompletionRate = calculateGroupCompletionRate(sub);
  const extraCompletionRate = calculateGroupCompletionRate(subapps);

  const { tierBadges } = badges;

  const renderChapterLink = useCallback(
    (chapter, className, locked = false) => {
      const classes = clsx('trainingCard__Animated', className);
      const locale = `${langFile}-${langFile.toUpperCase()}`;

      const { id, video } = chapter;

      let videoUrl = video;
      if (locale !== 'en-EN' && video && video.includes('.mp4')) {
        // Replace .mp4 with _{locale}.mp4
        videoUrl = video.replace(/.mp4$/, `_${locale}.mp4`);
      }

      return (
        <div className={classes} key={`ch${id}`} id={`ch${id}`} data-fancybox={videoUrl} data-src={videoUrl}>
          <TrainingCard appId={appId} chapter={chapter} redirPath="/main/training/chapter/" locked={locked} />
        </div>
      );
    },
    [appId, code, langFile],
  );

  const renderExtraCategoryLink = useCallback(
    (module, className, locked = false) => {
      const classes = clsx('trainingCard__Animated', className);
      const { id } = module;
      return (
        <div className={classes} key={`ex${id}`}>
          <TrainingExtraCard
            appId={appId}
            module={module}
            redirPath="/main/training/subapps/"
            locked={locked}
            foundationLocked={foundationalCompletionRate < 100}
          />
        </div>
      );
    },
    [appId, foundationalCompletionRate],
  );

  // render function for an entire category. (to subpage)
  const renderCategoryLink = useCallback(
    (category, className, locked = false) => {
      const classes = clsx('trainingCard__Animated', className);

      const { id, chapterIDs } = category;
      const categoryChapters = chapters.filter(({ id: chapterID }) => chapterIDs.includes(chapterID));
      return (
        <div className={classes} key={`ca${id}`}>
          <TrainingCategoryCard
            appId={appId}
            category={category}
            chapters={categoryChapters}
            redirPath="/main/training/extra"
            locked={locked}
            foundationLocked={foundationalCompletionRate < 100}
          />
        </div>
      );
    },
    [appId, chapters, foundationalCompletionRate],
  );

  // render function for a group. Checks if the group should be rendered as a whole category, or if it should render the chapters individually. style is added to allow for styling.
  const renderGroup = useCallback(
    (group, style) => {
      const { categoryIDs = [] } = group || {};
      const groupCategories = categories.filter(({ id }) => categoryIDs.includes(id));

      const links = groupCategories.flatMap((category) => {
        const { openableSection, chapterIDs } = category;

        // openableSection is a boolean that determines if the category is 'openable', meaning it should be render the category as a whole. Else, it should render the chapters directly.
        if (openableSection) {
          const totalNumberOfPages = chapters
            .filter(({ id }) => chapterIDs.includes(id))
            .reduce((acc, chapter) => {
              const { chapterItemCount } = chapter;
              return acc + Number(chapterItemCount);
            }, 0);
          const locked = totalNumberOfPages === 0 || foundationalCompletionRate < 100;
          return renderCategoryLink(category, clsx('specialization', `trainingGrid__training-card--${style}`), locked);
        }

        return chapters
          .filter((chapter) => {
            const { id } = chapter;
            return chapterIDs.includes(id);
          })
          .map((chapter) => {
            const { id } = chapter;
            const locked = isLockedChapter(id);
            return renderChapterLink(chapter, `trainingGrid__training-card--${style}`, locked);
          });
      });
      return links;
    },
    [categories, chapters, isLockedChapter, renderCategoryLink, renderChapterLink, foundationalCompletionRate],
  );

  // render function for the extra learning category. (to subpage)
  const renderSubappsLinks = useCallback(
    (style) => {
      const links = subapps.map((module) => {
        const { chapterItemCount: totalPages } = module || {};
        const locked = totalPages === 0 || foundationalCompletionRate < 100; // lock if there are no pages within the chapter
        return renderExtraCategoryLink(module, `trainingGrid__training-card--${style}`, locked);
      });
      return links;
    },
    [renderExtraCategoryLink, subapps, foundationalCompletionRate],
  );

  const foundationalLinks = useMemo(() => renderGroup(main, 'main'), [main, renderGroup]);
  const specialistLinks = useMemo(() => renderGroup(sub, 'sub'), [renderGroup, sub]);
  const extraLearningLinks = useMemo(() => renderSubappsLinks('extra'), [renderSubappsLinks]);

  // opens the accordion panels based on the completion rate of the chapters
  const opened = useMemo(() => {
    const requiredProgress = 95;

    if (
      foundationalCompletionRate >= requiredProgress &&
      specialistCompletionRate < requiredProgress &&
      extraCompletionRate < requiredProgress
    )
      return ['specialized', 'extra'];

    // Open all tabs if all categories are completed
    if (
      foundationalCompletionRate >= requiredProgress &&
      specialistCompletionRate >= requiredProgress &&
      extraCompletionRate >= requiredProgress
    )
      return ['foundational', 'specialized', 'extra'];

    // Open the extra tab both the foundational and specialist tabs are completed but the extra tab is not
    if (
      foundationalCompletionRate >= requiredProgress &&
      specialistCompletionRate >= requiredProgress &&
      extraCompletionRate < requiredProgress
    )
      return ['extra'];

    // Open the specialist tab if the foundational tab is completed, but the specialist tab is not (even if the extra tab is completed)
    if (foundationalCompletionRate >= requiredProgress && specialistCompletionRate < requiredProgress)
      return ['specialized'];

    // Open the foundational tab if none of the above conditions are met
    return ['foundational'];
  }, [extraCompletionRate, foundationalCompletionRate, specialistCompletionRate]);

  return (
    <Spring from={{ transform: 'translateY(30%)', opacity: 0 }} to={{ transform: 'translateY(0%)', opacity: 1 }}>
      {() => (
        <React.Fragment>
          {pointsHandlerLoaded && (
            <BadgeDisplay
              tierBadges={tierBadges}
              tierIndex={tierIndex}
              tiers={tiers}
              totalPoints={totalPoints}
              layout="pillar"
              position="right"
            />
          )}
          {chaptersLoaded && (
            <React.Fragment>
              <IntroChapterPopup chapters={chapters} />
              <TrainingGridAccordion
                {...{ diplomaProgressRate, opened, foundationalLinks, specialistLinks, extraLearningLinks }}
              />
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </Spring>
  );
};

export default TrainingGrid;
